import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import CreatableSelect from 'react-select/creatable';
import '../styles/AddCompanyForm.css';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext';
import AirDatePicker from '../components/ui/AirDatePicker';
import Swal from 'sweetalert2';
import InvoiceRenderer from '../components/invoice/InvoiceRenderer';

const getDummyMappedInvoiceData = (formData) => {
    return {
        buyer: {
            name: 'AeroTech Manufacturing Ltd',
            realName: 'AeroTech Manufacturing Ltd',
            address: 'Plot No. 42, GIDC Estate, Phase-III',
            cityState: 'Ahmedabad, Gujarat - 382445',
            placeOfSupply: 'Gujarat',
            stateCode: '24',
            gstin: '24ABCDE1234F1Z0',
            pan: 'ABCDE1234F'
        },
        consignee: {
            name: 'AeroTech Manufacturing Ltd (Warehouse)',
            address: 'Plot No. 42, GIDC Estate, Phase-III',
            cityState: 'Ahmedabad, Gujarat - 382445',
            placeOfSupply: 'Gujarat',
            stateCode: '24',
            gstin: '24ABCDE1234F1Z0',
            pan: 'ABCDE1234F'
        },
        invoiceNo: 'ASE/26-27/0042',
        invoiceDate: new Date().toLocaleDateString('en-GB'),
        eWayBillNo: '171234567890',
        sealNo: 'SEAL88374',
        vehicleNo: 'GJ-01-XX-9999',
        transporter: 'Vikas Roadlines',
        lrNo: 'LR-998822',
        containerNo: 'CON-776655',
        paymentTerms: '30 Days Credit',
        extraChargesName: 'Freight Charges',
        items: [
            {
                srNo: 1,
                product: 'Glazed Vitrified Tile (800x1200 mm)',
                category: 'Ceramic Tiles',
                model: '800x1200 mm',
                hsn: '69072100',
                grade: 'Premium',
                qty: 120,
                rate: '450.00',
                amount: '54000.00'
            },
            {
                srNo: 2,
                product: 'Super-Bond Epoxy Adhesive (5kg)',
                category: 'Adhesives',
                model: '5 kg Bucket',
                hsn: '35069190',
                grade: 'Standard',
                qty: 10,
                rate: '1250.00',
                amount: '12500.00'
            }
        ],
        totals: {
            qty: 130,
            subTotal: '66500.00',
            discount: '1500.00',
            discountPercent: '2.25',
            discountType: '%',
            insurance: '200.00',
            insurancePercent: '0.3',
            extra: '500.00',
            taxableAmount: '65700.00',
            tax1Rate: 9,
            tax1: '5913.00',
            tax2Rate: 9,
            tax2: '5913.00',
            totalTax: '11826.00',
            words: 'Seventy Seven Thousand Five Hundred Twenty Six Rupees Only',
            grandTotal: '77526.00',
            isLocal: true
        },
        bank: {
            accountName: formData.companyBankAccountName || 'ASENCIA ENTERPRISE LTD',
            name: formData.companyBankName || 'HDFC Bank',
            accountNumber: formData.companyBankAccountNumber || '50200012345678',
            ifsc: formData.companyBankIfscCode || 'HDFC0001234'
        }
    };
};

const AddCompanyForm = ({ isOpen, onClose, company, isPage }) => {
    const { fetchMyPermissions } = usePermissionContext();
    const [formData, setFormData] = useState({
        companyName: '',
        companyPersonName: '',
        companyEmail: '',
        companyMobileNumber_1: '',
        companyMobileNumber_2: '',
        companyWebsiteUrl: '',
        companyGstNumber: '',
        companyPanCardNumber: '',
        companyAadharCardNumber: '',
        companyCountry: '',
        companyState: '',
        companyCity: '',
        companyStateCode: '',
        companyPinCode: '',
        companyMapUrl: '',
        companyBankName: '',
        companyBankAccountName: '',
        companyBankAccountNumber: '',
        companySwiftCode: '',
        companyIbanNo: '',
        companyBankIfscCode: '',
        companyBackground: 1, // Default 1 (Domestic)
        invoiceFormat: 'format1',
        eWayBillUsername: '',
        eWayBillPassword: '',
        eiUsername: '',
        eiPassword: '',
        companyAddress: '',
        companyBankAddress: '',
        adminName: '',
        adminEmail: '',
        adminMobile: '',
        adminPassword: '',
        // Images (Base64 Strings)
        companyLogoImage: '',
        companyLetterHeadHeaderImage: '',
        companyLetterHeadFooterImage: '',
        companyDigitalSignature: '',
        companyPanCardFrontImage: '',
        companyPanCardBackImage: '',
        companyAadharCardFrontImage: '',
        companyAadharCardBackImage: '',
        companyOtherDocuments_1: '',
        companyOtherDocuments_2: '',
        companyOtherDocuments_3: '',
        companyOtherDocuments_4: '',
        companyOtherDocuments_5: '',
        countryId: '',
        stateId: '',
        cityId: '',
        isActive: true
    });

    const [fileData, setFileData] = useState({});
    const [isVerifyingGst, setIsVerifyingGst] = useState(false);
    const [previewFormat, setPreviewFormat] = useState(null);

    // --- Plan States ---
    const [plans, setPlans] = useState([]);
    const [planData, setPlanData] = useState({
        planId: '',
        planName: '',
        planPrice: 0,
        planDiscount: 0,
        couponCode: '',
        couponDiscountAmount: 0,
        finalPrice: 0,
        planStartDate: new Date().toISOString().split('T')[0],
        planExpiryDate: ''
    });

    const [couponInput, setCouponInput] = useState('');
    const [couponApplied, setCouponApplied] = useState(false);
    const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
    const [isRenewing, setIsRenewing] = useState(false);

    // --- Dropdown Data States ---
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState({ country: false, state: false, city: false });

    // --- Fetch Data ---
    useEffect(() => {
        fetchCountries();
        fetchPlans();
    }, []);

    useEffect(() => {
        if (company) {
            setFormData({
                companyName: company.companyName || '',
                companyPersonName: company.companyPersonName || '',
                companyEmail: company.companyEmail || '',
                companyMobileNumber_1: company.companyMobileNumber_1 || '',
                companyMobileNumber_2: company.companyMobileNumber_2 || '',
                companyWebsiteUrl: company.companyWebsiteUrl || '',
                companyGstNumber: company.companyGstNumber || '',
                companyPanCardNumber: company.companyPanCardNumber || '',
                companyAadharCardNumber: company.companyAadharCardNumber || '',
                companyCountry: company.companyCountry || 'India',
                companyState: company.companyState || '',
                companyCity: company.companyCity || '',
                companyStateCode: company.companyStateCode || '',
                companyPinCode: company.companyPinCode || '',
                companyMapUrl: company.companyMapUrl || '',
                companyBankName: company.companyBankName || '',
                companyBankAccountName: company.companyBankAccountName || '',
                companyBankAccountNumber: company.companyBankAccountNumber || '',
                companySwiftCode: company.companySwiftCode || '',
                companyIbanNo: company.companyIbanNo || '',
                companyBankIfscCode: company.companyBankIfscCode || '',
                companyBackground: company.companyBackground || 1,
                invoiceFormat: company.invoiceFormat || 'format1',
                eWayBillUsername: company.eWayBillUsername || '',
                eWayBillPassword: company.eWayBillPassword || '',
                eiUsername: company.eiUsername || '',
                eiPassword: company.eiPassword || '',
                companyAddress: company.companyAddress || '',
                companyBankAddress: company.companyBankAddress || '',
                adminName: '',
                adminEmail: '',
                adminMobile: '',
                adminPassword: '',
                companyLogoImage: company.companyLogoImage || '',
                companyLetterHeadHeaderImage: company.companyLetterHeadHeaderImage || '',
                companyLetterHeadFooterImage: company.companyLetterHeadFooterImage || '',
                companyDigitalSignature: company.companyDigitalSignature || '',
                companyPanCardFrontImage: company.companyPanCardFrontImage || '',
                companyPanCardBackImage: company.companyPanCardBackImage || '',
                companyAadharCardFrontImage: company.companyAadharCardFrontImage || '',
                companyAadharCardBackImage: company.companyAadharCardBackImage || '',
                companyOtherDocuments_1: company.companyOtherDocuments_1 || '',
                companyOtherDocuments_2: company.companyOtherDocuments_2 || '',
                companyOtherDocuments_3: company.companyOtherDocuments_3 || '',
                companyOtherDocuments_4: company.companyOtherDocuments_4 || '',
                companyOtherDocuments_5: company.companyOtherDocuments_5 || '',
                countryId: company.countryId || '',
                stateId: company.stateId || '',
                cityId: company.cityId || '',
                isActive: company.isActive !== undefined ? company.isActive : true
            });

            // Pre-fill plan data if editing
            if (company.planId) {
                const expiryDateStr = company.planExpiryDate
                    ? new Date(company.planExpiryDate).toISOString().split('T')[0]
                    : '';
                const startDateStr = company.planStartDate
                    ? new Date(company.planStartDate).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0];
                setPlanData({
                    planId: company.planId._id || company.planId || '',
                    planName: company.planName || '',
                    planDurationDays: company.planDurationDays || '',
                    planPrice: company.planPrice || 0,
                    planDiscount: company.planDiscount || 0,
                    couponCode: company.couponCode || '',
                    couponDiscountAmount: company.couponDiscountAmount || 0,
                    finalPrice: company.finalPrice || 0,
                    planStartDate: startDateStr,
                    planExpiryDate: expiryDateStr
                });
                if (company.couponCode) {
                    setCouponInput(company.couponCode);
                    setCouponApplied(true);
                }
            }

            if (company.countryId) {
                fetchStates(company.countryId);
            }
            if (company.stateId) {
                fetchCities(company.stateId);
            }
        }
    }, [company]);

    const fetchCountries = async () => {
        setIsLoading(prev => ({ ...prev, country: true }));
        try {
            const data = await fetchApi('/country');
            const list = Array.isArray(data) ? data : (data.countries || []);
            setCountries(list.map(c => ({
                label: c.country_name || c.countryName,
                value: c.id || c.countryId,
                code: c.country_code || c.countryCode
            })));
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
        setIsLoading(prev => ({ ...prev, country: false }));
    };

    const fetchPlans = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/plan`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                // Handle both paginated and non-paginated responses
                if (data.plans && Array.isArray(data.plans)) {
                    setPlans(data.plans);
                } else if (Array.isArray(data)) {
                    setPlans(data);
                }
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
        }
    };

    const fetchStates = async (countryId) => {
        if (!countryId) return;
        setIsLoading(prev => ({ ...prev, state: true }));
        try {
            const data = await fetchApi(`/state?country_id=${countryId}`);
            const list = Array.isArray(data) ? data : (data.states || []);
            setStates(list.map(s => ({
                label: s.state_name || s.stateName,
                value: s.id || s.stateId
            })));
        } catch (error) {
            console.error("Error fetching states:", error);
        }
        setIsLoading(prev => ({ ...prev, state: false }));
    };

    const fetchCities = async (stateId) => {
        if (!stateId) return;
        setIsLoading(prev => ({ ...prev, city: true }));
        try {
            const data = await fetchApi(`/city?state_id=${stateId}`);
            const list = Array.isArray(data) ? data : (data.cities || []);
            setCities(list.map(c => ({
                label: c.city_name || c.cityName,
                value: c.id || c.cityId
            })));
        } catch (error) {
            console.error("Error fetching cities:", error);
        }
        setIsLoading(prev => ({ ...prev, city: false }));
    };

    // --- Handle Create ---

    const handleCreateCountry = async (inputValue) => {
        setIsLoading(prev => ({ ...prev, country: true }));
        // Simple heuristic: use name as code (uppercase, first 3 chars or full if needed), 
        // to make it "one-click" as requested.
        const newCode = inputValue.substring(0, 3).toUpperCase();

        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/country`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ country_name: inputValue, country_code: newCode })
            });
            const data = await response.json();
            if (response.ok) {
                const c = data.country || data;
                toast.success(`Country "${inputValue}" Added`);
                const newOption = { label: c.country_name || c.countryName, value: c.id || c.countryId, code: c.country_code || c.countryCode };
                setCountries(prev => [...prev, newOption]);
                // Select it
                setFormData(prev => ({
                    ...prev,
                    countryId: newOption.value,
                    companyCountry: newOption.label,
                    stateId: '', companyState: '',
                    cityId: '', companyCity: ''
                }));
                setStates([]);
                setCities([]);
                // No states to fetch for new country yet
            } else {
                toast.error(data.message || "Failed to add country");
            }
        } catch (error) {
            console.error("Error adding country:", error);
            toast.error("Server error");
        }
        setIsLoading(prev => ({ ...prev, country: false }));
    };

    const handleCreateState = async (inputValue) => {
        if (!formData.countryId) {
            toast.error("Please select a country first");
            return;
        }
        setIsLoading(prev => ({ ...prev, state: true }));
        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/state`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ state_name: inputValue, country_id: formData.countryId })
            });
            const data = await response.json();
            if (response.ok) {
                const s = data.state || data;
                toast.success(`State "${inputValue}" Added`);
                const newOption = { label: s.state_name || s.stateName, value: s.id || s.stateId };
                setStates(prev => [...prev, newOption]);
                setFormData(prev => ({
                    ...prev,
                    stateId: newOption.value,
                    companyState: newOption.label,
                    cityId: '', companyCity: ''
                }));
                // No cities yet
                setCities([]);
            } else {
                toast.error(data.message || "Failed to add state");
            }
        } catch (error) {
            console.error("Error adding state:", error);
            toast.error("Server error");
        }
        setIsLoading(prev => ({ ...prev, state: false }));
    };

    const handleCreateCity = async (inputValue) => {
        if (!formData.stateId) {
            toast.error("Please select a state first");
            return;
        }
        setIsLoading(prev => ({ ...prev, city: true }));
        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/city`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ city_name: inputValue, state_id: formData.stateId })
            });
            const data = await response.json();
            if (response.ok) {
                const c = data.city || data;
                toast.success(`City "${inputValue}" Added`);
                const newOption = { label: c.city_name || c.cityName, value: c.id || c.cityId };
                setCities(prev => [...prev, newOption]);
                setFormData(prev => ({
                    ...prev,
                    cityId: newOption.value,
                    companyCity: c.city_name || c.cityName
                }));
            } else {
                toast.error(data.message || "Failed to add city");
            }
        } catch (error) {
            console.error("Error adding city:", error);
            toast.error("Server error");
        }
        setIsLoading(prev => ({ ...prev, city: false }));
    };

    // --- Change Handlers ---

    const handleCountrySelect = (option) => {
        if (option) {
            setFormData(prev => ({
                ...prev,
                countryId: option.value,
                companyCountry: option.label,
                stateId: '', companyState: '',
                cityId: '', companyCity: ''
            }));
            fetchStates(option.value);
            setStates([]); // temporary clear
        } else {
            setFormData(prev => ({
                ...prev,
                countryId: '',
                companyCountry: '',
                stateId: '', companyState: '',
                cityId: '', companyCity: ''
            }));
            setStates([]);
            setCities([]);
        }
    };

    const handleStateSelect = (option) => {
        if (option) {
            setFormData(prev => ({
                ...prev,
                stateId: option.value,
                companyState: option.label,
                cityId: '', companyCity: ''
            }));
            fetchCities(option.value);
            setCities([]); // temporary clear
        } else {
            setFormData(prev => ({
                ...prev,
                stateId: '',
                companyState: '',
                cityId: '', companyCity: ''
            }));
            setCities([]);
        }
    };

    const handleCitySelect = (option) => {
        if (option) {
            setFormData(prev => ({
                ...prev,
                cityId: option.value,
                companyCity: option.label
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                cityId: '',
                companyCity: ''
            }));
        }
    };


    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.toLowerCase().includes('adhar') || name.toLowerCase().includes('aadhar')) {
            let val = value.replace(/\D/g, '');
            if (val.length > 12) val = val.substring(0, 12);
            let formatted = val;
            if (val.length > 4) formatted = val.substring(0, 4) + ' ' + val.substring(4);
            if (val.length > 8) formatted = formatted.substring(0, 9) + ' ' + val.substring(8);
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        if (
            name.toLowerCase().includes('pincode') ||
            name.toLowerCase().includes('pin_code') ||
            name.toLowerCase().includes('accountnumber') ||
            name.toLowerCase().includes('account_number') ||
            name.toLowerCase().includes('accountno') ||
            name.toLowerCase().includes('account_no')
        ) {
            const onlyDigits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            return;
        }
        if (name.toLowerCase().includes('gst')) {
            let val = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (val.length > 15) val = val.substring(0, 15);
            setFormData(prev => ({ ...prev, [name]: val }));
            return;
        }
        if (name.toLowerCase().includes('vehicle')) {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }
        if (name.toLowerCase().includes('mobile')) {
            const onlyDigits = value.replace(/\D/g, '');
            if (onlyDigits.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            }
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const verifyGst = async (gstNo) => {
        if (!gstNo || gstNo.length !== 15 || isVerifyingGst) return;
        setIsVerifyingGst(true);
        try {
            const data = await fetchApi(`/gst/verify/${gstNo}`);

            if (data && data.success && data.data) {
                const gstData = data.data;
                toast.success(`GST verified: ${gstData.legalName || gstData.tradeName}`);

                const indiaOption = countries.find(c => c.label && c.label.toLowerCase() === 'india');
                let finalCountryId = indiaOption ? indiaOption.value : '';

                if (!finalCountryId) {
                    try {
                        const token = sessionStorage.getItem('token');
                        const cRes = await fetch(`${API_BASE_URL}/country`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ country_name: 'India', country_code: 'IND' })
                        });
                        const cData = await cRes.json();
                        if (cRes.ok && cData) {
                            const c = cData.country || cData;
                            finalCountryId = c.id || c.countryId;
                            setCountries(prev => [...prev, { label: 'India', value: finalCountryId, code: 'IND' }]);
                        }
                    } catch (e) { console.error("Auto add country error", e); }
                }

                let finalStateId = '';
                const gstStateName = gstData.state;
                if (finalCountryId && gstStateName) {
                    try {
                        const sData = await fetchApi(`/state?country_id=${finalCountryId}`);
                        const stateList = Array.isArray(sData) ? sData : (sData.states || []);
                        const existingState = stateList.find(s => {
                            const name = s.state_name || s.stateName;
                            return name && name.toLowerCase() === gstStateName.toLowerCase();
                        });

                        if (existingState) {
                            finalStateId = existingState.id || existingState.stateId;
                            setStates(stateList.map(s => ({ label: s.state_name || s.stateName, value: s.id || s.stateId })));
                        } else {
                            const token = sessionStorage.getItem('token');
                            const addSRes = await fetch(`${API_BASE_URL}/state`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({ state_name: gstStateName, country_id: finalCountryId })
                            });
                            const addSData = await addSRes.json();
                            if (addSRes.ok && addSData) {
                                const s = addSData.state || addSData;
                                finalStateId = s.id || s.stateId;
                                setStates(prev => [...prev, { label: gstStateName, value: finalStateId }]);
                            }
                        }
                    } catch (e) { console.error("Auto state err", e); }
                }

                let finalCityId = '';
                const gstCityName = gstData.city;
                if (finalStateId && gstCityName) {
                    try {
                        const ciData = await fetchApi(`/city?state_id=${finalStateId}`);
                        const cityList = Array.isArray(ciData) ? ciData : (ciData.cities || []);
                        const existingCity = cityList.find(c => {
                            const name = c.city_name || c.cityName;
                            return name && name.toLowerCase() === gstCityName.toLowerCase();
                        });

                        if (existingCity) {
                            finalCityId = existingCity.id || existingCity.cityId;
                            setCities(cityList.map(c => ({ label: c.city_name || c.cityName, value: c.id || c.cityId })));
                        } else {
                            const token = sessionStorage.getItem('token');
                            const addCiRes = await fetch(`${API_BASE_URL}/city`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify({ city_name: gstCityName, state_id: finalStateId })
                            });
                            const addCiData = await addCiRes.json();
                            if (addCiRes.ok && addCiData) {
                                const c = addCiData.city || addCiData;
                                finalCityId = c.id || c.cityId;
                                setCities(prev => [...prev, { label: gstCityName, value: finalCityId }]);
                            }
                        }
                    } catch (e) { console.error("Auto city err", e); }
                }

                const stateCode = gstNo.substring(0, 2);

                setFormData(prev => ({
                    ...prev,
                    companyName: gstData.tradeName || gstData.legalName || '',
                    companyPersonName: gstData.legalName || gstData.tradeName || '',
                    companyPanCardNumber: gstData.panNumber || '',
                    companyAddress: gstData.address || '',
                    companyPinCode: gstData.pincode || '',
                    companyCity: gstData.city || '',
                    companyState: gstData.state || '',
                    companyStateCode: stateCode,
                    companyCountry: 'India',
                    countryId: finalCountryId,
                    stateId: finalStateId,
                    cityId: finalCityId
                }));
            } else {
                toast.error(data.message || 'GST verification failed');
            }
        } catch (error) {
            toast.error(error.message || "Failed to verify GST number");
        } finally {
            setIsVerifyingGst(false);
        }
    };

    const handleGstChange = (e) => {
        const val = e.target.value.toUpperCase();
        setFormData(prev => ({
            ...prev,
            companyGstNumber: val,
            companyName: '',
            companyPersonName: '',
            companyPanCardNumber: '',
            companyAddress: '',
            companyPinCode: '',
            companyCity: '',
            companyState: '',
            companyStateCode: '',
            companyCountry: ''
        }));

        if (val.length === 15) {
            verifyGst(val);
        }
    };

    // --- Plan Selection Handler ---
    const calculateFinalPrice = (price, planDiscount, coupon) => {
        let p_val = Number(price) || 0;
        let d_val = Number(planDiscount) || 0;
        let basePrice = p_val * (1 - (d_val / 100));
        let couponDiscount = 0;

        if (coupon) {
            let cv_val = Number(coupon.discountValue) || 0;
            if (coupon.discountType === 'percentage') {
                couponDiscount = basePrice * (cv_val / 100);
            } else {
                couponDiscount = cv_val;
            }
        }

        return {
            couponDiscount,
            finalPrice: Math.max(0, basePrice - couponDiscount)
        };
    };

    const handlePlanChange = (e) => {
        const selectedId = e.target.value;
        setCouponApplied(false);
        setCouponInput('');
        if (!selectedId) {
            setPlanData(prev => ({
                ...prev,
                planId: '',
                planName: '',
                planDurationDays: '',
                planPrice: 0,
                planDiscount: 0,
                couponCode: '',
                couponDiscountAmount: 0,
                finalPrice: 0,
                planExpiryDate: ''
            }));
            return;
        }
        const selectedPlan = plans.find(p => String(p._id || p.id) === String(selectedId));
        if (selectedPlan) {
            const startDate = planData.planStartDate ? new Date(planData.planStartDate) : new Date();
            const expiry = new Date(startDate);
            expiry.setDate(expiry.getDate() + selectedPlan.planDurationDays);
            const expiryStr = expiry.toISOString().split('T')[0];
            const { couponDiscount, finalPrice } = calculateFinalPrice(selectedPlan.planPrice, selectedPlan.planDiscount, null);

            setPlanData(prev => ({
                ...prev,
                planId: selectedPlan._id,
                planName: selectedPlan.planName,
                planDurationDays: selectedPlan.planDurationDays,
                planPrice: selectedPlan.planPrice || 0,
                planDiscount: selectedPlan.planDiscount || 0,
                couponCode: '',
                couponDiscountAmount: couponDiscount,
                finalPrice: finalPrice,
                planExpiryDate: expiryStr
            }));
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponInput) {
            toast.error("Please enter a coupon code");
            return;
        }
        if (!planData.planId) {
            toast.error("Please select a plan first");
            return;
        }

        setIsValidatingCoupon(true);
        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/coupon/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    couponCode: couponInput,
                    planId: planData.planId,
                    companyId: company?._id
                })
            });

            const data = await response.json();
            if (data.valid) {
                const { couponDiscount, finalPrice } = calculateFinalPrice(planData.planPrice, planData.planDiscount, data.coupon);
                setPlanData(prev => ({
                    ...prev,
                    couponCode: data.coupon.couponCode,
                    couponDiscountAmount: couponDiscount,
                    finalPrice: finalPrice
                }));
                setCouponApplied(true);
                toast.success(data.message);
            } else {
                toast.error(data.message || "Invalid coupon");
            }
        } catch (error) {
            console.error("Error validating coupon:", error);
            toast.error("Failed to validate coupon");
        } finally {
            setIsValidatingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        const { couponDiscount, finalPrice } = calculateFinalPrice(planData.planPrice, planData.planDiscount, null);
        setPlanData(prev => ({
            ...prev,
            couponCode: '',
            couponDiscountAmount: 0,
            finalPrice: finalPrice
        }));
        setCouponApplied(false);
        setCouponInput('');
        toast.info("Coupon removed");
    };

    const handleStartDateChange = (newStartDate) => {
        if (!newStartDate) return;

        setPlanData(prev => {
            let updatedExpiry = prev.planExpiryDate;
            if (prev.planDurationDays) {
                const startDate = new Date(newStartDate);
                const expiry = new Date(startDate);
                expiry.setDate(expiry.getDate() + parseInt(prev.planDurationDays));
                updatedExpiry = expiry.toISOString().split('T')[0];
            }
            return {
                ...prev,
                planStartDate: newStartDate,
                planExpiryDate: updatedExpiry
            };
        });
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = async (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            const file = files[0];
            const maxSize = 2500 * 1024; // 2500 KB in bytes

            if (file.size > maxSize) {
                toast.error("image is too large");
                e.target.value = ''; // Reset input
                return;
            }

            setFileData(prev => ({ ...prev, [name]: file }));
            try {
                const base64 = await convertToBase64(file);
                setFormData(prev => ({ ...prev, [name]: base64 }));
            } catch (error) {
                console.error("Error converting file:", error);
                toast.error("Error processing file");
            }
        }
    };

    const handleSubmit = async (e) => {

        if (e) e.preventDefault();
        const conf = await Swal.fire({
            title: 'Save Data?',
            text: 'Are you sure you want to save this data?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#00334e',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'No, Keep Editing'
        });
        if (!conf.isConfirmed) return;
        e.preventDefault();

        const missingFields = [];
        if (!formData.companyName) missingFields.push("Company Name");
        if (!formData.companyPersonName) missingFields.push("Person Name");
        if (!formData.companyEmail) missingFields.push("Email");
        if (!formData.companyMobileNumber_1) missingFields.push("Mobile 1");
        if (!formData.countryId) missingFields.push("Country");
        if (!formData.stateId) missingFields.push("State");
        if (!formData.cityId) missingFields.push("City");
        if (!formData.companyStateCode) missingFields.push("State Code");
        if (!formData.companyPinCode) missingFields.push("Pin Code");
        if (!formData.companyAddress) missingFields.push("Company Address");
        if (!formData.companyLogoImage) missingFields.push("Logo Image");
        if (!company && !formData.adminName) missingFields.push("Admin Name");
        if (!company && !formData.adminEmail) missingFields.push("Admin Email");
        if (!company && !formData.adminMobile) missingFields.push("Admin Mobile");
        if (!company && !formData.adminPassword) missingFields.push("Admin Password");

        if (missingFields.length > 0) {
            toast.error(`Please fill required fields: ${missingFields.join(', ')}`);
            return;
        }

        const data = new FormData();
        // Append text fields
        Object.keys(formData).forEach(key => {
            // Don't append if it's an image field we are sending via files
            if (![
                'companyLogoImage',
                'companyLetterHeadHeaderImage',
                'companyLetterHeadFooterImage',
                'companyDigitalSignature',
                'companyPanCardFrontImage',
                'companyPanCardBackImage',
                'companyAadharCardFrontImage',
                'companyAadharCardBackImage',
                'companyOtherDocuments_1',
                'companyOtherDocuments_2',
                'companyOtherDocuments_3',
                'companyOtherDocuments_4',
                'companyOtherDocuments_5'
            ].includes(key)) {
                data.append(key, formData[key]);
            }
        });

        // Append plan fields
        if (planData.planId) {
            data.append('planId', planData.planId);
            data.append('planStartDate', planData.planStartDate);
            data.append('planExpiryDate', planData.planExpiryDate);
            if (planData.couponCode) {
                data.append('couponCode', planData.couponCode);
            }
        }

        // Append files
        Object.keys(fileData).forEach(key => {
            data.append(key, fileData[key]);
        });

        try {
            const token = sessionStorage.getItem('token');
            const companyId = company ? (company._id || company.id) : null;

            if (company && !companyId) {
                toast.error("Invalid Company ID. Please refresh and try again.");
                return;
            }

            const url = company ? `${API_BASE_URL}/company/${companyId}` : `${API_BASE_URL}/company`;
            const method = company ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: data
            });

            const resData = await response.json();

            if (response.ok) {
                toast.success(company ? "Company Updated Successfully" : "Company Added Successfully");
                await fetchMyPermissions();
                onClose();
            } else {
                // Return detailed error info in toast
                const detailedError = (resData.error && typeof resData.error === 'string')
                    ? resData.error
                    : (resData.message || 'Server error');

                toast.error(`${detailedError}${resData.errorName ? ` (${resData.errorName})` : ''}`, {
                    autoClose: 8000 // Show for longer
                });
                console.error("Server Error Detail:", resData);
            }
        } catch (error) {
            console.error("Error saving company:", error);
            toast.error(`Client Error: ${error.message}`);
        }
    };

    if (!isOpen) return null;

    const sectionStyle = {
        gridColumn: '1 / -1',
        fontSize: '1.1rem',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '1rem',
        marginTop: '1.5rem',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: '0.5rem'
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '0.375rem',
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e1'
            }
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        })
    };

    const requiredStar = <span style={{ color: 'red', marginLeft: '2px' }}>*</span>;

    const renderImagePreview = (fieldName, label, isRequired = false) => (
        <div className="company-field">
            <label>{label} {isRequired && requiredStar}</label>
            <input
                type="file"
                name={fieldName}
                onChange={handleFileChange}
                accept="image/*,.webp"
                required={isRequired && !formData[fieldName]}
            />
            {formData[fieldName] && (
                <div className="image-preview-box" style={{ marginTop: '10px' }}>
                    <img
                        src={formData[fieldName].startsWith('data:') || formData[fieldName].startsWith('http') ? formData[fieldName] : `${API_BASE_URL.replace('/api', '')}/${formData[fieldName].replace(/\\/g, '/').startsWith('/') ? formData[fieldName].replace(/\\/g, '/').substring(1) : formData[fieldName].replace(/\\/g, '/')}`}
                        alt="Preview"
                        style={{ width: '80px', height: '80px', objectFit: 'cover', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className={isPage ? "list-page-container" : "modal-overlay"}>
            <div className={isPage ? "" : "modal"}>
                <div className="modal-header">
                    <h2 className="modal-title">{company ? 'Update Company' : 'Add Company'}</h2>
                    {!isPage && <button className="modal-close" onClick={onClose}>&times;</button>}
                </div>
                <div className="company-form-body">
                    <form onSubmit={handleSubmit}>
                        <div className="company-grid">

                            {/* --- Section 1: Company Personal Information --- */}
                            <h3 style={{ ...sectionStyle, marginTop: 0 }}>Company Personal Information</h3>

                            <div className="company-field" style={{ gridColumn: '1 / -1' }}>
                                <label>GST Number</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input
                                        type="text"
                                        name="companyGstNumber"
                                        value={formData.companyGstNumber}
                                        onChange={handleGstChange}
                                        placeholder="Enter 15-digit GST Number"
                                        maxLength="15"
                                        style={{ textTransform: 'uppercase', flex: 1 }}
                                    />
                                    <button
                                        type="button"
                                        className="list-page-btn list-page-btn-add"
                                        onClick={() => verifyGst(formData.companyGstNumber)}
                                        disabled={isVerifyingGst}
                                        style={{ margin: 0, whiteSpace: 'nowrap', opacity: isVerifyingGst ? 0.7 : 1, cursor: isVerifyingGst ? 'not-allowed' : 'pointer' }}
                                    >
                                        {isVerifyingGst ? 'Verifying...' : 'Verify GST'}
                                    </button>
                                </div>
                            </div>

                            <div className="company-field">
                                <label>Company Name {requiredStar}</label>
                                <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required />
                            </div>
                            <div className="company-field">
                                <label>Company Owner Name {requiredStar}</label>
                                <input type="text" name="companyPersonName" value={formData.companyPersonName} onChange={handleChange} required />
                            </div>
                            <div className="company-field">
                                <label>Email {requiredStar}</label>
                                <input type="email" name="companyEmail" value={formData.companyEmail} onChange={handleChange} required />
                            </div>
                            <div className="company-field">
                                <label>Mobile 1 {requiredStar}</label>
                                <input type="text" name="companyMobileNumber_1" value={formData.companyMobileNumber_1} onChange={handleChange} required />
                            </div>
                            <div className="company-field">
                                <label>Mobile 2</label>
                                <input type="text" name="companyMobileNumber_2" value={formData.companyMobileNumber_2} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>Website URL</label>
                                <input type="text" name="companyWebsiteUrl" value={formData.companyWebsiteUrl} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>PAN Card Number</label>
                                <input type="text" name="companyPanCardNumber" value={formData.companyPanCardNumber} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>Aadhar Card Number</label>
                                <input type="text" name="companyAadharCardNumber" value={formData.companyAadharCardNumber} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>Background</label>
                                <select name="companyBackground" value={formData.companyBackground} onChange={handleChange}>
                                    <option value={1}>Domestic</option>
                                    <option value={2}>International</option>
                                </select>
                            </div>
                            <div className="company-field">
                                <label>Status</label>
                                <select 
                                    name="isActive" 
                                    value={formData.isActive !== undefined ? String(formData.isActive) : "true"} 
                                    onChange={(e) => {
                                        const val = e.target.value === 'true';
                                        setFormData(prev => ({ ...prev, isActive: val }));
                                    }}
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Deactive</option>
                                </select>
                            </div>
                            <div className="invoice-format-selector">
                                <label className="format-label">Invoice Layout Format</label>
                                <div className="format-grid">
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format1' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format1' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-1</span>
                                            <p className="format-desc">Standard table layout, monochrome print-friendly design.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format1'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format1' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format2' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format2' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-2</span>
                                            <p className="format-desc">Premium card-style design, Outfit font, and deep blue accents.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format2'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format2' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format3' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format3' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-3</span>
                                            <p className="format-desc">Compact POS format, high-contrast monochrome design.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format3'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format3' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format4' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format4' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-4</span>
                                            <p className="format-desc">Elegant rounded borders, well-structured multi-section layout.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format4'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format4' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format5' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format5' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-5</span>
                                            <p className="format-desc">Minimalist black and white design with clean, dense tabular layout.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format5'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format5' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format6' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format6' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-6</span>
                                            <p className="format-desc">Premium blue-themed layout with structured transport, buyer, and amount summaries.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format6'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format6' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format7' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format7' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-7</span>
                                            <p className="format-desc">Modern blue-accented header layout featuring integrated bank and totals grid with customized terms.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format7'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format7' && <span className="check-badge">✓</span>}
                                    </div>
                                    <div 
                                        className={`format-card ${formData.invoiceFormat === 'format8' ? 'selected' : ''}`}
                                        onClick={() => setFormData(prev => ({ ...prev, invoiceFormat: 'format8' }))}
                                        type="button"
                                    >
                                        <div>
                                            <span className="format-title">Format-8</span>
                                            <p className="format-desc">Professional gray-themed print layout with comprehensive invoice/buyer details, grade, size and totals.</p>
                                        </div>
                                        <button 
                                            type="button" 
                                            className="preview-btn" 
                                            onClick={(e) => { e.stopPropagation(); setPreviewFormat('format8'); }}
                                        >
                                            🔍 View Sample
                                        </button>
                                        {formData.invoiceFormat === 'format8' && <span className="check-badge">✓</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="company-field">
                                <label>EWay Bill Username</label>
                                <input type="text" name="eWayBillUsername" value={formData.eWayBillUsername} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>EWay Bill Password</label>
                                <input type="text" name="eWayBillPassword" value={formData.eWayBillPassword} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>E-Invoice Username</label>
                                <input type="text" name="eiUsername" value={formData.eiUsername} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>E-Invoice Password</label>
                                <input type="text" name="eiPassword" value={formData.eiPassword} onChange={handleChange} />
                            </div>

                            {/* --- Section 1.5: Company Admin Information --- */}
                            {!company && (
                                <>
                                    <h3 style={sectionStyle}>Company Admin Information</h3>
                                    <div className="company-field">
                                        <label>Admin Name {requiredStar}</label>
                                        <input type="text" name="adminName" value={formData.adminName} onChange={handleChange} placeholder="Admin Person Name" required />
                                    </div>
                                    <div className="company-field">
                                        <label>Admin Email {requiredStar}</label>
                                        <input type="email" name="adminEmail" value={formData.adminEmail} onChange={handleChange} placeholder="Admin Login Email" required />
                                    </div>
                                    <div className="company-field">
                                        <label>Admin Mobile {requiredStar}</label>
                                        <input type="text" name="adminMobile" value={formData.adminMobile} onChange={handleChange} placeholder="Admin Mobile No." required />
                                    </div>
                                    <div className="company-field">
                                        <label>Admin Password {requiredStar}</label>
                                        <input type="password" name="adminPassword" value={formData.adminPassword} onChange={handleChange} placeholder="Admin Password" required />
                                    </div>
                                </>
                            )}

                            {/* --- Section 2: Company Address Information --- */}
                            <h3 style={sectionStyle}>Company Address Information</h3>

                            <div className="company-field">
                                <label>Country {requiredStar}</label>
                                <CreatableSelect
                                    isClearable
                                    isDisabled={isLoading.country}
                                    isLoading={isLoading.country}
                                    onChange={handleCountrySelect}
                                    onCreateOption={handleCreateCountry}
                                    options={countries}
                                    value={countries.find(c => c.value === formData.countryId) || (formData.companyCountry ? { label: formData.companyCountry, value: formData.countryId } : null)}
                                    placeholder="Select or Add Country"
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div className="company-field">
                                <label>State {requiredStar}</label>
                                <CreatableSelect
                                    isClearable
                                    isDisabled={!formData.countryId || isLoading.state}
                                    isLoading={isLoading.state}
                                    onChange={handleStateSelect}
                                    onCreateOption={handleCreateState}
                                    options={states}
                                    value={states.find(s => s.value === formData.stateId) || (formData.companyState ? { label: formData.companyState, value: formData.stateId } : null)}
                                    placeholder="Select or Add State"
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div className="company-field">
                                <label>City {requiredStar}</label>
                                <CreatableSelect
                                    isClearable
                                    isDisabled={!formData.stateId || isLoading.city}
                                    isLoading={isLoading.city}
                                    onChange={handleCitySelect}
                                    onCreateOption={handleCreateCity}
                                    options={cities}
                                    value={cities.find(c => c.value === formData.cityId) || (formData.companyCity ? { label: formData.companyCity, value: formData.cityId } : null)}
                                    placeholder="Select or Add City"
                                    styles={customSelectStyles}
                                />
                            </div>

                            <div className="company-field">
                                <label>State Code {requiredStar}</label>
                                <input type="text" name="companyStateCode" value={formData.companyStateCode} onChange={handleChange} required />
                            </div>
                            <div className="company-field">
                                <label>Pin Code {requiredStar}</label>
                                <input type="text" name="companyPinCode" value={formData.companyPinCode} onChange={handleChange} required />
                            </div>
                            <div className="company-field">
                                <label>Map URL</label>
                                <input type="text" name="companyMapUrl" value={formData.companyMapUrl} onChange={handleChange} />
                            </div>
                            <div className="company-field full-width">
                                <label>Company Address {requiredStar}</label>
                                <textarea name="companyAddress" value={formData.companyAddress} onChange={handleChange} rows="3" required></textarea>
                            </div>

                            {/* --- Section 3: Company Bank Information --- */}
                            <h3 style={sectionStyle}>Company Bank Information</h3>

                            <div className="company-field">
                                <label>Bank Name</label>
                                <input type="text" name="companyBankName" value={formData.companyBankName} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>Bank Account Name</label>
                                <input type="text" name="companyBankAccountName" value={formData.companyBankAccountName} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>Bank Account Number</label>
                                <input type="text" name="companyBankAccountNumber" value={formData.companyBankAccountNumber} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>Swift Code</label>
                                <input type="text" name="companySwiftCode" value={formData.companySwiftCode} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>IBAN No</label>
                                <input type="text" name="companyIbanNo" value={formData.companyIbanNo} onChange={handleChange} />
                            </div>
                            <div className="company-field">
                                <label>IFSC Code</label>
                                <input type="text" name="companyBankIfscCode" value={formData.companyBankIfscCode} onChange={handleChange} />
                            </div>
                            <div className="company-field full-width">
                                <label>Bank Address</label>
                                <textarea name="companyBankAddress" value={formData.companyBankAddress} onChange={handleChange} rows="3"></textarea>
                            </div>

                            {/* --- Section 4: Plan / Subscription --- */}
                            <h3 style={sectionStyle}>Plan / Subscription</h3>

                            <div className="company-field">
                                <label>Plan Name</label>
                                <select
                                    value={planData.planId}
                                    onChange={handlePlanChange}
                                    style={{ width: '100%' }}
                                    disabled={!!company && !isRenewing}
                                >
                                    <option value="">-- Select Plan --</option>
                                    {plans.map(p => (
                                        <option key={p._id} value={p._id}>
                                            {p.planName} ({p.planDurationDays} days)
                                        </option>
                                    ))}
                                </select>
                                {!!company && !isRenewing && (
                                    <button
                                        type="button"
                                        onClick={() => setIsRenewing(true)}
                                        style={{
                                            marginTop: '8px',
                                            padding: '4px 12px',
                                            fontSize: '12px',
                                            backgroundColor: '#1e3a8a',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Renew or Change Plan
                                    </button>
                                )}
                            </div>

                            <div className="company-field">
                                <label>Plan Start Date</label>
                                <AirDatePicker
                                    name="planStartDate"
                                    value={planData.planStartDate}
                                    onChange={handleStartDateChange}
                                    disabled={!!company && !isRenewing}
                                    placeholder="Select Start Date"
                                />
                            </div>

                            <div className="company-field">
                                <label>Plan Duration (Days)</label>
                                <input
                                    type="text"
                                    value={planData.planDurationDays ? `${planData.planDurationDays} days` : ''}
                                    readOnly
                                    placeholder="Auto-calculated from plan"
                                    style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                                />
                            </div>

                            <div className="company-field">
                                <label>Plan Expiry Date</label>
                                <input
                                    type="text"
                                    value={planData.planExpiryDate
                                        ? new Date(planData.planExpiryDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
                                        : ''}
                                    readOnly
                                    placeholder="Auto-calculated (start date + plan days)"
                                    style={{
                                        backgroundColor: '#f8fafc',
                                        cursor: 'not-allowed',
                                        color: planData.planExpiryDate
                                            ? (new Date(planData.planExpiryDate) < new Date() ? '#dc2626' : '#16a34a')
                                            : '#64748b'
                                    }}
                                />
                            </div>

                            <div className="company-field">
                                <label>Plan Price (₹)</label>
                                <input
                                    type="text"
                                    value={`₹${planData.planPrice || 0}`}
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed' }}
                                />
                            </div>

                            <div className="company-field">
                                <label>Discount (%)</label>
                                <input
                                    type="text"
                                    value={`${planData.planDiscount || 0}%`}
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc', cursor: 'not-allowed', color: '#1e3a8a' }}
                                />
                            </div>

                            <div className="company-field">
                                <label>Amount to Pay (₹)</label>
                                <input
                                    type="text"
                                    value={`₹${planData.finalPrice || 0}`}
                                    readOnly
                                    style={{ backgroundColor: '#f0fdf4', cursor: 'not-allowed', color: '#16a34a', fontWeight: 'bold' }}
                                />
                            </div>

                            <div className="company-field full-width">
                                <label>Apply Coupon Code</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <input
                                        type="text"
                                        value={couponInput}
                                        onChange={(e) => setCouponInput(e.target.value)}
                                        placeholder="Enter coupon code"
                                        disabled={couponApplied || !planData.planId || (!!company && !isRenewing)}
                                        style={{ textTransform: 'uppercase', flex: 1 }}
                                    />
                                    {couponApplied ? (
                                        <button
                                            type="button"
                                            onClick={handleRemoveCoupon}
                                            style={{ backgroundColor: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', padding: '0 15px', borderRadius: '8px', cursor: 'pointer' }}
                                        >
                                            Remove
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleApplyCoupon}
                                            disabled={isValidatingCoupon || !planData.planId || (!!company && !isRenewing)}
                                            style={{ backgroundColor: (!!company && !isRenewing) ? '#94a3b8' : '#1e3a8a', color: 'white', border: 'none', padding: '0 20px', borderRadius: '8px', cursor: (!!company && !isRenewing) ? 'not-allowed' : 'pointer' }}
                                        >
                                            {isValidatingCoupon ? '...' : 'Apply'}
                                        </button>
                                    )}
                                </div>
                                {couponApplied && planData.couponDiscountAmount > 0 && (
                                    <div style={{ marginTop: '5px', fontSize: '13px', color: '#16a34a', fontWeight: '600' }}>
                                        Coupon Applied! You saved ₹{planData.couponDiscountAmount.toFixed(2)}
                                    </div>
                                )}
                            </div>

                            {/* --- Section 5: Images --- */}
                            <h3 style={sectionStyle}>Images</h3>

                            {renderImagePreview('companyLogoImage', 'Logo Image', true)}
                            {renderImagePreview('companyLetterHeadHeaderImage', 'Header Image (size:1920x190)')}
                            {renderImagePreview('companyLetterHeadFooterImage', 'Footer Image')}
                            {renderImagePreview('companyDigitalSignature', 'Digital Signature')}
                            {renderImagePreview('companyPanCardFrontImage', 'PAN Front')}
                            {renderImagePreview('companyPanCardBackImage', 'PAN Back')}
                            {renderImagePreview('companyAadharCardFrontImage', 'Aadhar Front')}
                            {renderImagePreview('companyAadharCardBackImage', 'Aadhar Back')}
                        </div>

                        <div className="company-form-actions">
                            <button type="submit" className="btn-submit">Submit</button>
                            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
            
            {/* Modal Sample Preview */}
            {previewFormat && (
                <div className="invoice-preview-modal-overlay" onClick={() => setPreviewFormat(null)}>
                    <div className="invoice-preview-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="invoice-preview-modal-header">
                            <h3>Sample Invoice Preview - {previewFormat.toUpperCase().replace('FORMAT', 'FORMAT ')} Layout</h3>
                            <button className="invoice-preview-modal-close" onClick={() => setPreviewFormat(null)}>&times;</button>
                        </div>
                        <div className="invoice-preview-modal-body">
                            <InvoiceRenderer
                                data={{
                                    Company: {
                                        company_name: formData.companyName || 'Asencia Enterprise Ltd',
                                        company_address: formData.companyAddress || '101, Corporate Hub, Morbi, Gujarat',
                                        company_mobile_1: formData.companyMobileNumber_1 || '9409010040',
                                        company_email: formData.companyEmail || 'info@asencia.com',
                                        company_gst_number: formData.companyGstNumber || '24AAAAA0000A1Z5',
                                        company_pan_card_number: formData.companyPanCardNumber || 'AAAAA0000A',
                                        company_state: formData.companyState || 'Gujarat',
                                        company_pin_code: formData.companyPinCode || '363641',
                                        company_bank_name: formData.companyBankName || 'HDFC Bank',
                                        company_bank_account_name: formData.companyBankAccountName || 'ASENCIA ENTERPRISE LTD',
                                        company_bank_account_number: formData.companyBankAccountNumber || '50200012345678',
                                        company_bank_ifsc_code: formData.companyBankIfscCode || 'HDFC0001234',
                                        company_digital_signature: formData.companyDigitalSignature || null,
                                        company_letter_head_header_image: formData.companyLetterHeadHeaderImage || null,
                                        company_letter_head_footer_image: formData.companyLetterHeadFooterImage || null,
                                        invoiceFormat: previewFormat
                                    },
                                    companyName: formData.companyName || 'Asencia Enterprise Ltd',
                                    address: formData.companyAddress || '101, Corporate Hub, Morbi, Gujarat'
                                }}
                                invoiceData={getDummyMappedInvoiceData(formData)}
                                selectedPaymentType="Debit"
                                selectedInvoiceType="TAX INVOICE"
                                selectedCopyType="Original"
                                getImageUrl={(url) => url}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddCompanyForm;
